import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import * as Roles from '../../resources/UserRole';
import * as cartActions from '../../actions/cartActions';
import ItemsSummary from './ItemsSummary';
import Notifications from '../../services/Notifications';
import CartService from '../../services/CartService';
import CalendarService from '../../services/CalendarService';
import DateHelper from '../../helpers/DateHelper';
import { withSelectedCustomer } from '../../hooks/withSelectedCustomer';
import { withStorefrontConfig } from '../../hooks/StorefrontSettingsContext';
import { withCartConfig } from '../../hooks/CartConfigContext';

export class Cart extends React.Component {
    constructor(props) {
        super(props);

        const defaultFHC = this.props.StorefrontConfig.settingsList.DefaultFreightHandlingCode;
        const defaultBranch = this.props.StorefrontConfig.branches.find(branch => branch.isDefault === true)?.branchNumber || null;

        this.state = {
            selectedBranch: props.selectedBranch || defaultBranch,
            errors: [],
            orderNumber: 0,
            orderIsQuote: false,
            placingOrder: false,
            freightHandlingCode: props.freightHandlingCode || (props.selectedCustomer?.freightHandlingCode) || ((props?.freightHandlingCodes?.length || 0) > 0 ? props?.freightHandlingCodes[0]?.id : null) || defaultFHC,
            requestDate: null,
            isCashPayment: (props.selectedCustomer?.paymentTermId === props.StorefrontConfig.settingsList.CashPaymentTerm) ? true : false,
            branchVerificationItems: null,
            workdayCalendarSettings: {},
            loadingWorkdayCalendar: false,
            lastHeaderRequestDate: null,
            lastpayTerms: null,
            paymentInstrument: props.paymentInstrument ?? props.selectedCustomer?.paymentInstrument ?? ''

        };

        if (!(props && props.selectedCustomer && props.selectedCustomer.id)) {
            this.props.history.push({ pathname: '/' });
        }
    }

    componentDidMount() {
        this.mounted = true;        
        this.loadCustomerDefaultPaymentTerms();
        this.getWorkdayCalendarSettings();
    }

    componentDidUpdate(prevProps) {
        const prevCustId = prevProps.selectedCustomer?.id;
        const currCustId = this.props.selectedCustomer?.id;
        if (currCustId && currCustId !== prevCustId) {
            const nextFHC = this.props.selectedCustomer?.freightHandlingCode ?? this.props.StorefrontConfig.settingsList.DefaultFreightHandlingCode;
            const nextPaymentTermId = String(this.props.selectedCustomer?.paymentTerms ?? '');
            const nextInstrument = this.props.selectedCustomer?.paymentInstrument ?? '';
            const nextBranch = this.props.selectedBranch ?? this.props.StorefrontConfig.branches.find(b => b.isDefault === true)?.branchNumber ?? null;
            this.setState({
                freightHandlingCode: nextFHC,
                selectedBranch: nextBranch,
                isCashPayment: nextPaymentTermId === this.props.StorefrontConfig.settingsList.CashPaymentTerm,
                lastpayTerms: null
            },
                () => {
                    this.props.cartActions.UpdatePaymentTerms({
                        paymentTermId: nextPaymentTermId,
                        paymentInstrument: nextInstrument
                    });
                    this.props.cartActions.UpdateOrderHeader(
                        nextFHC,
                        this.state.requestDate ?? this.props.requestDate,
                        { paymentTermId: nextPaymentTermId, paymentInstrument: nextInstrument },
                        nextBranch
                    );
                    this.markRepriceMessageDisplayed(true);
                }
            );
        }
        if (prevProps.paymentTermId !== this.props.paymentTermId) {
            this.cashPaymentUpdated({ paymentTermId: this.props.paymentTermId });
        }
    }

    getWorkdayCalendarSettings = async () => {
        this.setState({ loadingWorkdayCalendar: true });

        try {
            const workdayCalendarResponse = await CalendarService.getWorkdayCalendarSettings(this.props.currentUser.storefrontNumber);

            if (!workdayCalendarResponse.isSuccessful) {
                this.notifyWorkdayCalendarFailure();
            }

            const { minDate, maxDate, disabledDates } = workdayCalendarResponse;
            this.setState({
                workdayCalendarSettings: {
                    minDate,
                    maxDate,
                    disabledDates,
                },
            });

            this.props.workdayCalendarSettings ? this.updateDate(this.props.requestDate) : this.updateDate(minDate);
            this.props.cartActions.UpdateWorkdayCalendarSettings(workdayCalendarResponse);
        } catch (error) {
            this.notifyWorkdayCalendarFailure();
        }

        this.setState({ loadingWorkdayCalendar: false });
    };

    notifyWorkdayCalendarFailure = () => {
        console.error("Unable to load the workday calendar. Default settings have been applied.");
    };

    loadCustomerDefaultPaymentTerms = () => {
        if (this.props.paymentTermId === null || this.props.paymentTermId === undefined || this.props.paymentTermId === "") {
            this.paymentTermsUpdated({paymentTermId: String(this.props.selectedCustomer.paymentTerms ?? ''),paymentInstrument: this.props.selectedCustomer.paymentInstrument ?? ''})
        }
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    allItemsAreAvailable() {
        if (this.props.fetching) {
            Notifications.message(this.props.CartConfig.labels.WaitForPriceAndAvailability)
            return false;
        }
        if (this.props.repriceMessageDisplayed) {
            Notifications.message(this.props.CartConfig.labels.UpateCartPriceMessage);
            return false;
        }

        for (let i = 0; i < this.props.items.length; i++) {
            let item = this.props.items[i];

            if (!item.availability) {
                Notifications.message(this.props.CartConfig.labels.WaitForPriceAndAvailability);
                return false;
            }

            if (item.availability === "Unavailable") {
                Notifications.error(this.props.CartConfig.labels.UnavailableItemsInCartMessage);
                return false;
            }

            if (item.quantity > item.quantityAvailable && item.product.stockingType === "U") {
                Notifications.error(this.props.CartConfig.labels.StockNotAvailableInCartMessage.replace("{0}", item.quantityAvailable).replace("{item}", item.product.sku));
                return false;
            }
        }
        return true;
    }

    fetchingCartPriceFinished = (res) => {
        if (res.items) {
            CartService.handleMultiplePriceAndAvailabilityResponse(res, this.props.cartActions);
        }
        else {
            Notifications.error(this.props.CartConfig.labels.FetchingPriceFailed);
        }
        this.setState({ fetching: false });
    }
    repriceCart = async () => {
        const {
            items,
            requestDate,
            CartConfig,
            StorefrontConfig,
            selectedCustomer,
            currentUser
        } = this.props;

        const { freightHandlingCode, selectedBranch } = this.state;

        this.updateDate(requestDate, true);
        const isFreightRequired = CartConfig.childComponents.FreightHandlingCodes.required === true;
        const isFreightEmpty = freightHandlingCode === null || freightHandlingCode === '0';
        if (isFreightEmpty && isFreightRequired) {
            return Notifications.error(CartConfig.labels.SelectFreightHandlingCode);
        }
        const emptyQuantityItems = items.filter(item => item.quantity === "" || item.quantity === 0);
        if (emptyQuantityItems.length > 0) {
            return Notifications.error(CartConfig.labels.QuantityRequired);
        }
        const branchItems = {
            branch: selectedBranch?.length > 0 ? selectedBranch : selectedCustomer.branchPlant,
            skus: items.map(item => item.product.sku)
        };

        let branchVerification = "";

        if (CartConfig.settings.UseDirectBranchVerification) {
            if (CartConfig.settings.UseDefaultBranchForVerification) {
                const defaultBranch = StorefrontConfig.branches.find(b => b.isDefault === "1");
                if (defaultBranch) {
                    branchItems.branch = defaultBranch.branchNumber;
                }
            }
            branchVerification = await CartService.itemBranchVerification(branchItems);
            this.setState({ branchVerificationItems: branchVerification });
        }
        if (branchVerification.length > 0) {
            const toastMessage = StorefrontConfig.settingsList.UseCartBranchSelection !== '0'
                ? CartConfig.labels.UnavailableAtSelectedBranchToast
                : CartConfig.labels.UnavailableAtBranchToast;

            return Notifications.error(`${toastMessage}${branchVerification}`);
        }

        if (StorefrontConfig.settingsList.UseCartBranchSelection !== '0') {
            let branch = selectedBranch;

            if (!branch && StorefrontConfig.branches.length > 0) {
                const defaultBranch = StorefrontConfig.branches.find(b => b.isDefault === true);
                if (defaultBranch) {
                    branch = defaultBranch.branchNumber;
                }
            }

            this.setState({ selectedBranch: branch });
        }
        const orderPayload = CartService.getSubmitOrderBodyEComm({
            ...this.props,
            docType: StorefrontConfig.settingsList.UseOrderType,
            implementationId: StorefrontConfig.storefront.implementationId,
            selectedCustomer,
            useCustomerInventoryPreference: CartConfig.settings.UseCustomerInventoryPreference,
            useBranchStockingType: CartConfig.settings.UseBranchStockingType,
            storefrontNumber: currentUser.storefront.number,
            priceCartType: StorefrontConfig.settingsList.UsePriceCartOrch === "1" ? "orch" : "bssv",
            selectedBranch: this.state.selectedBranch ?? null,
            factorVirtualLocations: StorefrontConfig.settingsList.ShowVirtualLocations == '1', 
            useAssignedBranches: StorefrontConfig.settingsList.UsesAssignedBranches == '1'
        });
        this.setState({ fetching: true });
        return CartService.fetchPriceCartEComm(orderPayload).then(this.fetchingCartPriceFinished);
    };
    
    reviewIfItemsAvailable = () => {
        if (this.allItemsAreAvailable()) {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            this.props.history.push({ pathname: '/cart/review', state: { orderIsQuote: false, dateFormat: this.props.StorefrontConfig.settingsList.DateFormat } });
        }
    }

    reviewQuote = () => {
        if (this.allItemsAreAvailable()) {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            this.props.history.push({ pathname: '/cart/review', state: { orderIsQuote: true } });
        }
    }

    updateDate = (date, reprice = false) => {
        if (!date) {
            this.setState({ requestDate: null });
            return;
        }        
        const newHeaderRequestDate = new Date(date).setHours(0, 0, 0, 0);
        const { lastHeaderRequestDate } = this.state;
        let isLastRequestDate = false;
        if (lastHeaderRequestDate) {
            if (newHeaderRequestDate !== lastHeaderRequestDate) {
                isLastRequestDate = true;
                this.markRepriceMessageDisplayed(true);
            }
        }
        this.setState({ requestDate: date, lastHeaderRequestDate: newHeaderRequestDate }, this.updateOrderHeader);

        if ((this.isRepriceMessageDisplayed() || isLastRequestDate) && this.props.StorefrontConfig.storefrontSettingsLoaded && this.props.CartConfig.cartSettingsLoaded) {
            this.props.items.forEach((item) => {

                var d = new Date();
                var headerRequestDate = new Date(date);
                headerRequestDate.setHours(0, 0, 0, 0);
                d.setHours(0, 0, 0, 0);
                var daysForMType = this.props.StorefrontConfig.settingsList.NoOfDaysForMType === undefined ? 0 : Number(this.props.StorefrontConfig.settingsList.NoOfDaysForMType);
                var daysForPType = this.props.StorefrontConfig.settingsList.NoOfDaysForPType === undefined ? 0 : Number(this.props.StorefrontConfig.settingsList.NoOfDaysForPType);

                var baselineDate = new Date();
                var chosenDate = new Date(item.requestedDeliveryDate != undefined ? item.requestedDeliveryDate : headerRequestDate);
                if (!reprice) {
                    chosenDate = headerRequestDate;
                }
                if (this.props.CartConfig.settings.UseStockingTypeForRequestDeliveryDate == true) {
                    if (headerRequestDate >= d) {
                        if ((item.requestedDeliveryDate > headerRequestDate) && reprice) {
                            baselineDate = item.requestedDeliveryDate;
                        }
                        else {
                            baselineDate = headerRequestDate;
                        }
                    }
                    else {
                        baselineDate = d;
                    }
                    if (item.product.stockingType == 'M') {
                        if (baselineDate) {
                            var dateComparator = DateHelper.addDays(headerRequestDate, daysForMType);
                            if (baselineDate > dateComparator) {
                                chosenDate = baselineDate;
                            }
                            else {
                                chosenDate = dateComparator;
                            }
                        }
                    }
                    else if (item.product.stockingType == 'P') {
                        if (baselineDate) {
                            var dateComparator = DateHelper.addDays(headerRequestDate, daysForPType);
                            if (baselineDate > dateComparator) {
                                chosenDate = baselineDate;
                            }
                            else {
                                chosenDate = dateComparator
                            }
                        }
                    }
                }

                item.requestedDeliveryDate = chosenDate;
                this.updateCartItem(item);
            });
        }
    }

    updateRepriceMessageDisplayed = () => {
        if (!this.isRepriceMessageDisplayed()) {
            this.markRepriceMessageDisplayed();
            this.removeRepriceMessageDisplayedFromHistory();
        }
    }

    isRepriceMessageDisplayed = () => {
        const { location, repriceMessageDisplayed } = this.props;
        return location.state?.repriceMessageDisplayed ?? repriceMessageDisplayed ?? true;
    }

    markRepriceMessageDisplayed = () => {
        this.props.cartActions.UpdateRepriceMessageDisplayed();
    }
    markRepriceMessageDisplayed = (repricevalue) => {
        this.props.cartActions.UpdateRepriceMessageDisplayed(repricevalue);
    }
    removeRepriceMessageDisplayedFromHistory = () => {
        const { location, history } = this.props;

        const updatedState = { ...location.state };
        delete updatedState.repriceMessageDisplayed;

        history.replace({
            ...location,
            state: updatedState,
        });
    }

    selectedBranchUpdate = (e) => {
        const newBranch = e.target.value !== "" ? e.target.value : this.props.StorefrontConfig.branches.find(x => x.isDefault === true);
        if (newBranch !== this.state.selectedBranch) {
            this.setState({ selectedBranch: newBranch }, this.updateOrderHeader);
            this.markRepriceMessageDisplayed(true);
        }
    };

    freightHandlingCodeUpdate = (e) => {
        const newFreightHandlingCode = e.target.value !== "" ? e.target.value : this.props.StorefrontConfig.settingsList.DefaultFreightHandlingCode;
        if (newFreightHandlingCode !== this.state.freightHandlingCode) {
            this.setState({ freightHandlingCode: newFreightHandlingCode }, this.updateOrderHeader);
            this.markRepriceMessageDisplayed(true);
        }
    }

    paymentTermsUpdated = (payTerms) => {
        const newpayTerms = String(payTerms?.paymentTermId ?? '');
        const { lastpayTerms } = this.state;

        if (!newpayTerms) return;

        if (newpayTerms !== lastpayTerms) {
            const instrument = payTerms.paymentInstrument?.trim() ?? this.props.selectedCustomer.paymentInstrument ?? '';
            this.props.cartActions.UpdatePaymentTerms({
                paymentTermId: newpayTerms,
                paymentInstrument: instrument
            });

            this.setState({
                lastpayTerms: newpayTerms
            });
        }

        this.cashPaymentUpdated(payTerms);
    };


    cashPaymentUpdated = (payTerms) => {
        if (this.props.currentUser.roles.includes(Roles.CAN_SELECT_CASH_PAYMENT)) {
            if (payTerms.paymentTermId === this.props.StorefrontConfig.settingsList.CashPaymentTerm) {
                this.setState({ isCashPayment: true });
            }
            else {
                this.setState({ isCashPayment: false });
            }
        }
    }

    updateOrderHeader = () => {
        const paymentTermId = String(this.props.paymentTermId ?? '').trim();
        const paymentInstrument = String(this.props.paymentInstrument ?? '');
        const paymentTerms = { paymentTermId: paymentTermId || String(this.props.selectedCustomer?.paymentTerms ?? ''),
            paymentInstrument: paymentInstrument || (this.props.selectedCustomer?.paymentInstrument ?? '')
        };

        const freightHandlingCode = this.state.freightHandlingCode || this.props.freightHandlingCode;
        const requestDate = this.state.requestDate || this.props.requestDate;
        const selectedBranch = this.state.selectedBranch || this.props.selectedBranch;

        this.props.cartActions.UpdateOrderHeader(freightHandlingCode, requestDate, paymentTerms, selectedBranch);
        this.updateRepriceMessageDisplayed();
    }

    addItemToCart = (item, localized) => {
        this.props.cartActions.AddItemToCart(item, localized);
    }

    removeItem = (id) => {
        this.props.cartActions.RemoveCartItem(id, () => { Notifications.message(this.props.CartConfig.labels.OrderUpdated); });
    }

    clearCart = () => {
        const localized = this.props.CartConfig.labels;
        Notifications.confirmation(localized.ConfirmClearCartAllItems, () => {
            this.props.cartActions.ClearCart(
                this.props.currentUser.id,
                null, null, null, () => { Notifications.message(this.props.CartConfig.labels.OrderUpdated); }
            );
        });
    }

    updateCartItem = (item) => {
        this.props.cartActions.UpdateCartItem(item);
        this.forceUpdate();
    }

    updateLineItemQuantity = (item, quantity) => {
        CartService.updateQuantity(item, quantity).then((res) => {
            if (res) {
                this.updateCartItem(res);
                Notifications.message(this.props.CartConfig.labels.OrderUpdated)
            }
            else {
                Notifications.error(this.props.CartConfig.labels.UnabletoUpdateQuantity);
            }
        });
    }

    render() {
        const { fetching, isCashPayment, freightHandlingCode, branchVerificationItems, selectedBranch, workdayCalendarSettings, loadingWorkdayCalendar } = this.state;
        const { currentUser, paymentTermId, requestDate, selectedCustomer, repriceMessageDisplayed, subTotal, currency, itemCount, items } = this.props;
        const { DateFormat, OrderBusinessUnit } = this.props.StorefrontConfig.settingsList;
        const branches = this.props.StorefrontConfig.settingsList.UsesAssignedBranches == "1" ?
            this.props.StorefrontConfig.branches.filter(x => this.props.currentUser.assignedBranches.includes(x.branchNumber) || x.isDefault === true)
            : this.props.StorefrontConfig.branches;
        return (
            <ItemsSummary

                key={itemCount}
                branches={branches}
                selectedBranch={selectedBranch}
                editingQuote={false}
                selectedCustomer={selectedCustomer}
                currentUser={currentUser}
                items={items}
                branchVerificationItems={branchVerificationItems}
                freightHandlingCode={freightHandlingCode}
                paymentInstrument={this.props.paymentInstrument}
                paymentTermId={paymentTermId}
                requestDate={requestDate}
                dateFormat={DateFormat}
                repriceMessageDisplayed={repriceMessageDisplayed}
                fetching={fetching}
                subTotal={subTotal}
                currency={currency}
                updateLineItemQuantity={this.updateLineItemQuantity}
                updateCartItem={this.updateCartItem}
                removeItem={this.removeItem}
                addItemToCart={this.addItemToCart}
                updateOrderHeader={this.updateOrderHeader}
                paymentTermsUpdated={this.paymentTermsUpdated}
                freightHandlingCodeUpdate={this.freightHandlingCodeUpdate}
                selectedBranchUpdate={this.selectedBranchUpdate}
                updateDate={this.updateDate}
                reviewQuote={this.reviewQuote}
                reviewIfItemsAvailable={this.reviewIfItemsAvailable}
                repriceCart={this.repriceCart}
                localized={this.props.CartConfig.labels}
                loadCustomerDefaultPaymentTerms={this.loadCustomerDefaultPaymentTerms}
                isCashPayment={isCashPayment}
                clearCart={this.clearCart}
                workdayCalendarSettings={workdayCalendarSettings}
                loadingWorkdayCalendar={loadingWorkdayCalendar}
                orderBusinessUnit={OrderBusinessUnit }
            />
        )
    }
}

Cart.propTypes = {
    cartActions: PropTypes.object,
    currentUser: PropTypes.object,
    fetching: PropTypes.bool,
    freightHandlingCode: PropTypes.string,
    items: PropTypes.array,
    paymentInstrument: PropTypes.string,
    paymentTermId: PropTypes.string,
    requestDate: PropTypes.object,
    selectedCustomer: PropTypes.object,
    subTotal: PropTypes.number,
    currency: PropTypes.string,
    itemCount: PropTypes.number,
    isCashPayment: PropTypes.bool,
    selectedBranch: PropTypes.string
};


function mapStateToProps(state) {
    return {
        currentUser: state.user.currentUser,
        fetching: state.cart.fetchingPricesAndAvailability.length !== 0,
        freightHandlingCode: state.cart.freightHandlingCode,
        items: state.cart.items,
        paymentInstrument: state.cart.paymentInstrument,
        paymentTermId: state.cart.paymentTermId,
        requestDate: state.cart.requestDate,
        selectedCustomer: state.user.selectedCustomer,
        repriceMessageDisplayed: state.cart.repriceMessageDisplayed,
        subTotal: state.cart.netSubtotal,
        currency: state.cart.netCurrency,
        itemCount: state.cart.items.length,
        selectedBranch: state.cart.selectedBranch,
        workdayCalendarSettings: state.cart.workdayCalendarSettings
    };
}

function mapDispatchToProps(dispatch) {
    return {
        cartActions: bindActionCreators(cartActions, dispatch),
    };
}

export default withRouter(connect(
    mapStateToProps,
    mapDispatchToProps
)(withStorefrontConfig(withSelectedCustomer(withCartConfig(Cart)))));